import { useDisclosure } from "@chakra-ui/react";
import {
  declareAiCapability,
  useProvideAiCapability,
} from "../../ai-omnibar/ai-capability-registry";
import DialerModal from "../../../shared/components/DialerModal";
import { useReactBridgeDispatchEvent } from "../../../shared/hooks/useReactBridgeEvent";
import { usePhoneNumberDialerPopup } from "../hooks/usePhoneNumberDialerPopup";
import useCallCenter from "../hooks/useCallCenter";
import useInviteToCallModal from "../hooks/useInviteToCallModal";

// The dialer describes itself here rather than anywhere central, because this listener is what
// actually opens it: where this component is not mounted, the capability correctly reports itself
// unavailable instead of the AI dispatching an event nothing handles.
declareAiCapability({
  id: "dialer.open",
  title: "Open the dialer",
  effect: "prefill",
  description:
    "Opens the phone dialer, optionally with a number already filled in. It does not place the call - the user presses dial.",
  location: "a popup over the current page",
  params: [
    {
      name: "phoneNumber",
      type: "text",
      required: false,
      description:
        "Number to fill in, digits only or as the user said it. Leave it out to open an empty dialer.",
    },
  ],
  examples: ["call 718-555-0134", "open the dialer"],
});

export default function PhoneNumberDialerPopupListener() {
  const dialerPopup = usePhoneNumberDialerPopup();
  const callCenter = useCallCenter();
  const inviteToCallModal = useInviteToCallModal();
  const disclosure = useDisclosure({
    isOpen: dialerPopup.state.isOpen,
    onClose: dialerPopup.close,
  });

  useReactBridgeDispatchEvent({
    eventName: "CALL_CENTER:OPEN_DIALER_POPUP",
    onEvent: (payload) => {
      if (callCenter.state.currentCall === null) {
        dialerPopup.open({ predefinedPhoneNumber: payload.predefinedPhoneNumber });
      } else {
        inviteToCallModal.onOpen();
      }
    },
  });

  useProvideAiCapability("dialer.open", (args) => {
    const phoneNumber = typeof args.phoneNumber === "string" ? args.phoneNumber : undefined;

    if (callCenter.state.currentCall !== null) {
      inviteToCallModal.onOpen();

      return "You are already on a call - opened the invite dialog instead";
    }

    dialerPopup.open({ predefinedPhoneNumber: phoneNumber });

    return phoneNumber === undefined ? "Opened the dialer" : `Opened the dialer for ${phoneNumber}`;
  });

  return (
    <DialerModal
      isOpen={disclosure.isOpen}
      predefinedPhoneNumber={dialerPopup.state.predefinedPhoneNumber}
      onClose={disclosure.onClose}
    />
  );
}
